import { useState, useEffect, useCallback } from 'react'
import SearchableSelect from './SearchableSelect'
import { searchCustomers, getCustomerLite } from '../lib/customerSearch'
import { clearCustomersByRoleCache } from '../hooks/useCustomersByRole'
import QuickAddCustomerDialog from './QuickAddCustomerDialog'

interface Props {
  value: string
  onChange: (id: string) => void
  role?: string
  // Z86: pick from ALL active customers (e.g. suppliers aren't a role in customer_address_types yet).
  anyRole?: boolean
  placeholder?: string
  disabled?: boolean
  onClose?: () => void
  className?: string
  // U1: opt-in, not the default -- most CustomerCombobox usages are inline
  // table-cell editors or filter dropdowns where a "create new" affordance
  // would be intrusive/out of place. Enable it where staff otherwise get
  // stuck unable to proceed at all without picking an existing customer
  // (e.g. the CI Upload wizard's Step 1).
  allowCreate?: boolean
  // Passed through to SearchableSelect: red border for a required-but-empty field.
  invalid?: boolean
}

export default function CustomerCombobox({
  value, onChange, role = 'Cus', anyRole = false,
  placeholder = '— Select customer —',
  disabled, onClose, className, allowCreate = false, invalid = false,
}: Props) {
  // Z88: server-side search (50 rows max per query) instead of loading every customer of the role.
  // The currently selected customer is fetched by id so it always has a label, even if it isn't in
  // the latest search results.
  const [selected, setSelected] = useState<{ id: string; label: string } | null>(null)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [quickAddInitialName, setQuickAddInitialName] = useState('')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!value) { setSelected(null); return }
    if (selected?.id === value) return
    let cancelled = false
    getCustomerLite(value).then(c => {
      if (!cancelled && c) setSelected({ id: c.id, label: `${c.code} – ${c.name}` })
    })
    return () => { cancelled = true }
  }, [value, selected?.id])

  const onSearch = useCallback(async (term: string) => {
    const rows = await searchCustomers({ role: anyRole ? undefined : role, term, limit: 50 })
    return rows.map(c => ({ id: c.id, label: `${c.code} – ${c.name}` }))
  }, [role, anyRole])

  return (
    <div
      className={className}
      onBlur={e => {
        if (onClose && !e.currentTarget.contains(e.relatedTarget as Node)) onClose()
      }}
    >
      <SearchableSelect
        options={[]}
        onSearch={onSearch}
        selectedOption={selected}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        allowCreate={allowCreate}
        invalid={invalid}
        onCreateClick={allowCreate ? (searchTerm) => { setQuickAddInitialName(searchTerm); setQuickAddOpen(true) } : undefined}
      />
      {allowCreate && (
        <QuickAddCustomerDialog
          isOpen={quickAddOpen}
          initialName={quickAddInitialName}
          role={role}
          onClose={() => setQuickAddOpen(false)}
          onSuccess={customer => {
            clearCustomersByRoleCache()
            setSelected({ id: customer.id, label: `${customer.code} – ${customer.name}` })
            onChange(customer.id)
          }}
        />
      )}
    </div>
  )
}
